"use client";

import { cn } from "@rallly/ui";
import * as React from "react";

const STORAGE_KEY = "zc-background-mode"; // shared with the ZC Knowledge Base

type Mode = "motion" | "static";

/**
 * Top-right STATIC / MOTION pill, mirroring the ZC Knowledge Base portal.
 * Writes the shared `zc-background-mode` localStorage key and reloads so
 * ZenVideoBackground re-evaluates its gates. Rendered only after mount:
 * the current mode lives in localStorage, which the server cannot know.
 */
export function ZenMotionToggle({ className }: { className?: string }) {
  const [mode, setMode] = React.useState<Mode | null>(null);

  React.useEffect(() => {
    setMode(
      window.localStorage.getItem(STORAGE_KEY) === "static"
        ? "static"
        : "motion",
    );
  }, []);

  if (!mode) return null;

  const select = (next: Mode) => {
    if (next === mode) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    window.location.reload();
  };

  const segment = (value: Mode, label: string) => (
    <button
      type="button"
      aria-pressed={mode === value}
      onClick={() => select(value)}
      className={cn(
        "rounded-full px-2.5 py-1 font-semibold text-[10px] uppercase tracking-[0.14em] transition-colors",
        mode === value
          ? "bg-[#C9A962] text-[#1E201F]"
          : "text-[#9B9483] hover:text-[#D4CBBA]",
      )}
    >
      {label}
    </button>
  );

  return (
    <fieldset
      className={cn(
        "fixed top-4 right-4 z-20 flex items-center gap-0.5 rounded-full border border-[#C9A962]/20 bg-[#141615]/85 p-0.5 backdrop-blur-sm",
        className,
      )}
      aria-label="Background mode"
    >
      {segment("static", "Static")}
      {segment("motion", "Motion")}
    </fieldset>
  );
}
